import { useState } from "react"
import { Megaphone } from "lucide-react"
import { cn } from "@vc/lib/utils"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useBreakoutBroadcastStore } from "@/features/meeting/store/breakout-broadcast.store"
import { ControlButton } from "../common/ControlButton"
import { BreakoutBroadcastViewer } from "./BreakoutBroadcastViewer"

/**
 * A participant's way into the host's breakout announcements.
 *
 * The host reaches the same thread through their rooms control; a participant
 * has no rooms control — deliberately, since they cannot move between rooms —
 * and so had no way to re-read an announcement once its toast had gone. This
 * sits beside Reactions, which is where the other "things that happen to me"
 * controls live.
 *
 * Built on ControlButton rather than a bare <button>: the surface, hover, size
 * and tooltip of every other control in the bar come from there, and rolling
 * them by hand is how this ended up the one icon in the row with no background.
 */
export function BreakoutBroadcastControl({ compact = false }: { compact?: boolean }) {
  const t = useTranslation()
  const [open, setOpen] = useState(false)
  // Unread, not total — the badge is a prompt to look, so it has to clear once
  // the thread has been looked at.
  const count = useBreakoutBroadcastStore((s) => Math.max(0, s.messages.length - s.readCount))

  return (
    <>
      <div className="relative">
        <ControlButton
          onClick={() => setOpen(true)}
          label={t("controlBar.broadcastFromHost")}
          compact={compact}
        >
          <Megaphone className={cn("text-white", compact ? "h-4 w-4" : "h-5 w-5")} />
        </ControlButton>
        {count > 0 && (
          <span className="pointer-events-none absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-amber-500 px-1 text-[10px] font-semibold text-white">
            {count > 9 ? "9+" : count}
          </span>
        )}
      </div>
      <BreakoutBroadcastViewer open={open} onOpenChange={setOpen} />
    </>
  )
}
